import React, { useCallback, useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
  Skeleton,
  useMediaQuery
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FolderIcon from '@mui/icons-material/Folder'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RestoreIcon from '@mui/icons-material/Restore'
import { toast } from 'react-toastify'
import APIService from 'services/APIService'

const LIBRARY_LABEL = 'Whole library'

const CYAN = '#33becc'
const CYAN_DARK = '#2aa6b3'
const CYAN_TINT = '#e6f7f9'

const describePath = path => path || `${LIBRARY_LABEL} (every folder)`

const SharePointChangeFolderModal = ({ open, onClose, customView, buildingPassportId, onChanged }) => {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const currentFolder = customView?.sharepointFolderPath || null

  const [step, setStep] = useState('browse')
  const [trail, setTrail] = useState([])
  const [folders, setFolders] = useState([])
  const [loadingFolders, setLoadingFolders] = useState(false)
  const [preview, setPreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const basePath = `/building-passport/${buildingPassportId}/custom-views/${customView?.id}`
  const browsingPath = trail.length ? trail[trail.length - 1].path : null
  const browsingWebUrl = trail.length ? trail[trail.length - 1].webUrl : null

  const loadFolders = useCallback(async path => {
    setLoadingFolders(true)
    setError(null)
    try {
      const response = await APIService().get(`${basePath}/sharepoint-folders`, {
        params: path ? { folder_path: path } : {}
      })
      setFolders(response.data?.data?.folders || [])
    } catch (err) {
      setFolders([])
      setError(err.response?.data?.message || 'We could not load the folders from SharePoint. Please try again.')
    } finally {
      setLoadingFolders(false)
    }
  }, [basePath])

  useEffect(() => {
    if (!open || !customView?.id) return
    setStep('browse')
    setTrail([])
    setPreview(null)
    setError(null)
    loadFolders(null)
  }, [open, customView?.id, loadFolders])

  const openFolder = folder => {
    setTrail(prev => [...prev, { name: folder.name, path: folder.path, webUrl: folder.webUrl }])
    loadFolders(folder.path)
  }

  const goToCrumb = index => {
    const next = index < 0 ? [] : trail.slice(0, index + 1)
    setTrail(next)
    loadFolders(next.length ? next[next.length - 1].path : null)
  }

  const reviewChange = async () => {
    setLoadingPreview(true)
    setError(null)
    setStep('confirm')
    try {
      const response = await APIService().get(`${basePath}/sharepoint-folder/preview`, {
        params: browsingPath ? { folder_path: browsingPath } : {}
      })
      setPreview(response.data?.data || null)
    } catch (err) {
      setError(err.response?.data?.message || 'We could not work out what would change. Please try again.')
    } finally {
      setLoadingPreview(false)
    }
  }

  const confirmChange = async () => {
    setSaving(true)
    setError(null)
    try {
      await APIService().put(`${basePath}/sharepoint-folder`, {
        folder_path: browsingPath,
        folder_web_url: browsingWebUrl
      })
      toast.success('Synced folder changed. We are re-syncing from the new folder now.')
      if (onChanged) onChanged()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'We could not change the folder. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const isSameAsCurrent = (browsingPath || null) === currentFolder

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={isMobile}
      maxWidth='sm'
      fullWidth
      aria-labelledby='sp-change-folder-title'
      sx={{ zIndex: 20010 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, px: 3, pt: 2.5, pb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography id='sp-change-folder-title' sx={{ fontSize: 20, fontWeight: 700, color: '#12132e' }}>
            {step === 'browse' ? 'Change synced folder' : 'Check the change'}
          </Typography>
          <Typography sx={{ fontSize: 15, color: '#4d4d56', mt: 0.5 }}>
            {step === 'browse'
              ? 'Choose the SharePoint folder this view should copy files from. Open a folder, then press "Sync this folder".'
              : 'Please read what will happen before you confirm.'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label='Close' sx={{ width: 44, height: 44 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 1 }}>
        {error && (
          <Alert severity='error' sx={{ mb: 2, fontSize: 15 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'browse' && (
          <>
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, backgroundColor: '#f6f6f8' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#6b6b76' }}>Currently syncing</Typography>
              <Typography sx={{ fontSize: 15, color: '#12132e', wordBreak: 'break-word' }}>
                {describePath(currentFolder)}
              </Typography>
            </Box>

            <Box
              component='nav'
              aria-label='Folder location'
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1.5 }}
            >
              <CrumbButton active={trail.length === 0} onClick={() => goToCrumb(-1)}>
                {LIBRARY_LABEL}
              </CrumbButton>
              {trail.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  <ChevronRightIcon sx={{ fontSize: 18, color: '#9a9aa5' }} />
                  <CrumbButton active={index === trail.length - 1} onClick={() => goToCrumb(index)}>
                    {crumb.name}
                  </CrumbButton>
                </React.Fragment>
              ))}
            </Box>

            <Box sx={{ border: '1px solid #e3e3e8', borderRadius: 1.5, overflow: 'hidden' }}>
              {loadingFolders ? (
                [0, 1, 2, 3, 4].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderTop: i ? '1px solid #efeff2' : 0 }}>
                    <Skeleton variant='rounded' width={24} height={24} animation='pulse' />
                    <Skeleton variant='text' width={`${45 + (i % 3) * 15}%`} height={24} animation='pulse' />
                  </Box>
                ))
              ) : folders.length === 0 ? (
                <Typography sx={{ px: 2, py: 2.5, fontSize: 15, color: '#4d4d56' }}>
                  There are no folders inside this one. You can still sync this folder.
                </Typography>
              ) : (
                folders.map((folder, index) => (
                  <Box
                    key={folder.id}
                    component='button'
                    type='button'
                    onClick={() => openFolder(folder)}
                    aria-label={`Open ${folder.name}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      minHeight: 48,
                      px: 2,
                      py: 1,
                      border: 0,
                      borderTop: index ? '1px solid #efeff2' : 0,
                      background: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      font: 'inherit',
                      '&:hover, &:focus-visible': { backgroundColor: CYAN_TINT, outline: 'none' }
                    }}
                  >
                    <FolderIcon sx={{ color: CYAN, fontSize: 24, flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: 15, color: '#12132e', wordBreak: 'break-word' }}>
                      {folder.name}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#6b6b76', flexShrink: 0 }}>Open</Typography>
                    <ChevronRightIcon sx={{ color: '#9a9aa5', flexShrink: 0 }} />
                  </Box>
                ))
              )}
            </Box>

            {!browsingPath && (
              <Alert severity='warning' sx={{ mt: 2, fontSize: 14 }}>
                Syncing the whole library copies every folder in it, which may include files for other buildings.
                Most Building Passports should sync just the folder for this building.
              </Alert>
            )}
          </>
        )}

        {step === 'confirm' && (
          <>
            <Box sx={{ mb: 2 }}>
              <SummaryRow label='From'>{describePath(currentFolder)}</SummaryRow>
              <SummaryRow label='To'>{describePath(browsingPath)}</SummaryRow>
            </Box>

            {loadingPreview || !preview ? (
              !error && (
                <Box>
                  <Skeleton variant='text' width='80%' height={28} animation='pulse' />
                  <Skeleton variant='text' width='70%' height={28} animation='pulse' />
                  <Skeleton variant='text' width='60%' height={28} animation='pulse' />
                </Box>
              )
            ) : (
              <>
                <OutcomeRow icon={<CheckCircleOutlineIcon sx={{ color: '#15803d' }} />}>
                  <Bold>About {preview.filesKept.toLocaleString('en-GB')} files</Bold> are already in the new folder.
                  They stay as they are and are not copied again.
                </OutcomeRow>
                {preview.filesRemoved > 0 && (
                  <OutcomeRow icon={<RemoveCircleOutlineIcon sx={{ color: '#c2410c' }} />}>
                    <Bold>About {preview.filesRemoved.toLocaleString('en-GB')} files</Bold> are outside the new folder.
                    They will be removed from this Building Passport.
                  </OutcomeRow>
                )}
                {preview.filesRestored > 0 && (
                  <OutcomeRow icon={<RestoreIcon sx={{ color: '#15803d' }} />}>
                    <Bold>About {preview.filesRestored.toLocaleString('en-GB')} files</Bold> removed by an earlier folder change
                    will come back. They are restored as they were, not copied again.
                  </OutcomeRow>
                )}
                <OutcomeRow icon={<AddCircleOutlineIcon sx={{ color: CYAN_DARK }} />}>
                  Any files in the new folder that are not here yet will be copied in.
                </OutcomeRow>
                <OutcomeRow icon={<InfoOutlinedIcon sx={{ color: '#6b6b76' }} />}>
                  The folder list in this view is rebuilt to match the new folder. Anyone restricted to certain folders keeps the same restrictions on those folders.
                </OutcomeRow>
                <Alert severity='success' icon={<CheckCircleOutlineIcon />} sx={{ mt: 1.5, fontSize: 15, fontWeight: 700 }}>
                  Nothing is deleted in SharePoint.
                </Alert>
                {preview.syncRunning && (
                  <Alert severity='info' sx={{ mt: 1, fontSize: 14 }}>
                    A sync is running for this view right now. Please wait for it to finish, then try again.
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1, flexWrap: 'wrap' }}>
        {step === 'browse' ? (
          <>
            <Button onClick={onClose} sx={{ minHeight: 44, fontSize: 15, color: '#4d4d56' }}>
              Cancel
            </Button>
            <Button
              variant='contained'
              disableElevation
              onClick={reviewChange}
              disabled={loadingFolders || isSameAsCurrent}
              sx={{ minHeight: 44, fontSize: 15, backgroundColor: CYAN, '&:hover': { backgroundColor: CYAN_DARK } }}
            >
              {isSameAsCurrent ? 'This folder is already synced' : 'Sync this folder'}
            </Button>
          </>
        ) : (
          <>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => { setStep('browse'); setError(null) }}
              disabled={saving}
              sx={{ minHeight: 44, fontSize: 15, color: '#4d4d56' }}
            >
              Back
            </Button>
            <Button
              variant='contained'
              disableElevation
              onClick={confirmChange}
              disabled={saving || loadingPreview || !preview || preview.syncRunning}
              sx={{ minHeight: 44, fontSize: 15, backgroundColor: CYAN, '&:hover': { backgroundColor: CYAN_DARK } }}
            >
              {saving ? 'Changing folder…' : 'Change folder'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

const CrumbButton = ({ active, onClick, children }) => (
  <Button
    size='small'
    onClick={onClick}
    disabled={active}
    sx={{
      minHeight: 36,
      px: 1,
      textTransform: 'none',
      fontSize: 14,
      fontWeight: active ? 700 : 500,
      color: CYAN_DARK,
      '&.Mui-disabled': { color: '#12132e' },
      maxWidth: '100%',
      wordBreak: 'break-word',
      textAlign: 'left'
    }}
  >
    {children}
  </Button>
)

const Bold = ({ children }) => (
  <Box component='span' sx={{ fontWeight: 700, color: '#12132e' }}>{children}</Box>
)

const OutcomeRow = ({ icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
    <Box sx={{ flexShrink: 0, display: 'flex', mt: '1px' }}>{icon}</Box>
    <Typography sx={{ fontSize: 15, lineHeight: 1.5, color: '#2a2a35' }}>{children}</Typography>
  </Box>
)

const SummaryRow =({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
    <Typography sx={{ width: 48, flexShrink: 0, fontSize: 14, fontWeight: 700, color: '#6b6b76' }}>{label}</Typography>
    <Typography sx={{ fontSize: 15, color: '#12132e', wordBreak: 'break-word' }}>{children}</Typography>
  </Box>
)

export default SharePointChangeFolderModal
